import { useEffect, useState } from 'react';
import { api, ShoulderTapPayload } from '../services/api';

const DEFAULT_POLL_INTERVAL_SECONDS = 15;
const OFFLINE_FAILURE_THRESHOLD = 3;

export interface ShoulderTapAnalysisState {
  tap: ShoulderTapPayload | null;
  isLoading: boolean;
  error: Error | null;
  /**
   * Tri-state health signal:
   *   - null   → not yet determined (before the first config/poll response)
   *   - false  → healthy (last observed request succeeded)
   *   - true   → offline (config fetch failed, or ≥ threshold consecutive poll failures)
   *
   * Consumers forwarding this to the host MUST skip the null state so the
   * host isn't told the service is healthy before we've verified it.
   */
  isOffline: boolean | null;
}

/**
 * Reactive shoulder-tap state for a given contactId.
 *
 * Fetches /shoulder-tap-configuration on mount (or when contactId
 * changes) to learn cadence and eligibility, then polls
 * /shoulder-tap-analysis on that cadence and exposes the latest tap.
 * Fail-closed: if the config request fails or returns enabled=false,
 * no polling starts and `tap` stays null.
 *
 * Repeated identical taps (same reason + message) don't produce a new
 * `tap` reference, so effects that depend on `tap` fire on real
 * transitions, not on every poll.
 *
 * `isOffline` flips true when the config fetch fails or after
 * OFFLINE_FAILURE_THRESHOLD consecutive poll failures, and flips back
 * to false on the next successful poll. Consumers can forward this to
 * the host so it can surface a degraded / unavailable indicator.
 */
export function useShoulderTapAnalysis(contactId?: string | null): ShoulderTapAnalysisState {
  const [tap, setTap] = useState<ShoulderTapPayload | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(contactId));
  const [error, setError] = useState<Error | null>(null);
  const [isOffline, setIsOffline] = useState<boolean | null>(null);

  useEffect(() => {
    if (!contactId) {
      setTap(null);
      setIsLoading(false);
      setError(null);
      setIsOffline(null);
      return;
    }

    setTap(null);
    setIsLoading(true);
    setError(null);
    setIsOffline(null);

    let cancelled = false;
    let timerId: ReturnType<typeof setTimeout> | null = null;
    let consecutiveFailures = 0;

    const start = async () => {
      const config = await api.getShoulderTapConfiguration(contactId);
      if (cancelled) return;

      if (!config) {
        setError(new Error('Failed to fetch shoulder-tap configuration'));
        setIsOffline(true);
        setIsLoading(false);
        return;
      }
      if (!config.enabled) {
        // The config endpoint responded successfully, so the service
        // is reachable — publish healthy even though we won't be
        // polling. Otherwise a prior "offline" observation could stay
        // latched on the host forever.
        setIsOffline(false);
        setIsLoading(false);
        return;
      }

      const intervalSeconds =
        config.poll_interval_seconds > 0
          ? config.poll_interval_seconds
          : DEFAULT_POLL_INTERVAL_SECONDS;

      // Recursive setTimeout instead of setInterval: a slow analysis
      // request must not stack a second concurrent request behind it.
      const schedulePoll = async () => {
        const result = await api.getShoulderTapAnalysis(contactId);
        if (cancelled) return;

        if (result === null) {
          setError(new Error('Failed to fetch shoulder-tap analysis'));
          consecutiveFailures += 1;
          if (consecutiveFailures >= OFFLINE_FAILURE_THRESHOLD) {
            setIsOffline(true);
          }
        } else {
          consecutiveFailures = 0;
          setIsOffline(false);
          setError(null);
          setTap((prev) => {
            // Preserve reference identity when the payload hasn't changed
            // so consumer effects don't re-fire on every poll.
            if (
              prev &&
              result.tap &&
              prev.reason === result.tap.reason &&
              prev.message === result.tap.message
            ) {
              return prev;
            }
            return result.tap;
          });
        }
        setIsLoading(false);

        if (!cancelled) {
          timerId = setTimeout(schedulePoll, intervalSeconds * 1000);
        }
      };

      schedulePoll();
    };

    start();

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [contactId]);

  return { tap, isLoading, error, isOffline };
}
